import React, { useState } from 'react';
import { formatDriverName } from '../utils/driverName';
import PhotoLightbox from './PhotoLightbox';

const AutoBadgeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M88 15H18C10 15 4 21 4 29V66C4 70 7 73 11 73H13" />
    <path d="M29 73H72" />
    <path d="M38 18V46C38 52 42 56 48 56H64V18" />
    <path d="M68 18V44H84L78 18H68Z" />
    <circle cx="21" cy="76" r="9" />
    <circle cx="87" cy="78" r="9" />
  </svg>
);

const PhoneCallIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const ChatBubbleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

const ShareIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
    <polyline points="16 6 12 2 8 6"/>
    <line x1="12" y1="2" x2="12" y2="15"/>
  </svg>
);

export default function CaptainHeaderCard({
  ride,
  driver,
  otp,
  t,
  onCall,
  onChat,
  onViewProfile,
  showOtp,
  showActions = true,
}) {
  const [photoFailed, setPhotoFailed] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(false);
  if (!ride?.driverName && !driver?.name) return null;
  const rawName = ride.driverName || driver?.name || '';
  const displayName = formatDriverName(rawName);
  const initial = rawName.replace(/^(Anna|Captain)\s+/i, '').trim().split(/\s+/)[0]?.charAt(0).toUpperCase() || 'A';
  const photoUrl = driver?.photoUrl || ride.driverPhoto;
  const photo = photoUrl && !photoFailed ? photoUrl : null;
  const rating = driver?.ratingAvg != null
    ? Number(driver.ratingAvg)
    : (ride.driverRatingAvg != null ? Number(ride.driverRatingAvg) : (ride.driverRating != null ? Number(ride.driverRating) : null));
  const totalRides = driver?.totalRides ?? driver?.totalCompletedRides ?? driver?.lifetimeRides ?? ride?.driverTotalRides ?? ride?.totalCompletedRides ?? ride?.lifetimeRides ?? ride?.driverCompletedRides ?? null;
  const plate = ride.vehicleNumber || driver?.vehicleNumber || '';
  const vehicleModel = ride.vehicleModel || driver?.vehicleModel || '';
  const otpCode = String(otp || '').replace(/\D/g, '').slice(0, 4);

  return (
    <div className="captain-card-10">
      <div className="captain-top-row">
        {/* Left: Driver details column */}
        <div className="captain-details-col">
          <div className="captain-identity-row">
            <div className="captain-avatar-wrap">
              {photo ? (
                <button
                  type="button"
                  className="captain-avatar-btn"
                  onClick={() => setPhotoPreview(true)}
                  aria-label={t('driver.viewPhoto') || 'View photo'}
                >
                  <img
                    className="captain-avatar-img"
                    src={photo}
                    alt={displayName}
                    onError={() => setPhotoFailed(true)}
                  />
                </button>
              ) : (
                <div className="captain-avatar-circle" aria-hidden>{initial}</div>
              )}
              <span className="captain-verified-tick" title={t('driver.verified')} aria-label={t('driver.verified')}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </span>
            </div>
            <div className="captain-name-wrap">
              <div className="captain-title">{displayName}</div>
            </div>
          </div>

          {plate && (
            <div className="captain-vehicle-row">
              <div className="captain-vehicle-ico">
                <AutoBadgeIcon />
              </div>
              <span className="captain-vehicle-plate">{plate}</span>
            </div>
          )}
        </div>

        {/* Right: Mint TRIP PIN Box */}
        {showOtp && otpCode.length === 4 && (
          <div className="captain-otp-box">
            <div className="captain-otp-header">
              <span>{t('active.tripPin')}</span>
              <span className="otp-lock">🔒</span>
            </div>
            <div className="captain-otp-digits">
              {otpCode.split('').map((digit, idx) => (
                <span key={idx} className="otp-char">{digit}</span>
              ))}
            </div>
            <span className="captain-otp-subtext">{t('active.tripPinHint')}</span>
          </div>
        )}
      </div>

      {/* Bottom Action buttons */}
      {showActions && (
        <div className="captain-actions-row">
          {(ride.driverPhone || driver?.phone) && onCall && (
            <button type="button" className="captain-btn-pill call" onClick={onCall}>
              <PhoneCallIcon />
              <span>{t('active.call')}</span>
            </button>
          )}
          {onChat && (
            <button type="button" className="captain-btn-pill chat" onClick={onChat}>
              <ChatBubbleIcon />
              <span>{t('chat.open')}</span>
            </button>
          )}
        </div>
      )}

      {onViewProfile && (
        <button
          type="button"
          className="captain-view-profile-btn full-width"
          onClick={onViewProfile}
        >
          <span>{t('active.viewAnnaProfile')}</span>
          <span className="chevron-right">›</span>
        </button>
      )}

      {photoPreview && photo && (
        <PhotoLightbox
          src={photo}
          alt={displayName}
          onClose={() => setPhotoPreview(false)}
          closeLabel={t('common.close')}
        />
      )}
    </div>
  );
}

